"use client";

import { useTranslations } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { useParams } from "next/navigation";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Globe } from "lucide-react";
import { cn } from "@/lib/utils";

const LOCALES = ["de", "en"] as const;

export function LanguageSwitcher({ variant = "dropdown" }: { variant?: "dropdown" | "toggle" }) {
    const t = useTranslations("common");
    const router = useRouter();
    const pathname = usePathname();
    const params = useParams();
    const currentLocale = (params.locale as string) ?? "de";

    function switchLocale(locale: string) {
        router.replace(pathname, { locale });
    }

    if (variant === "toggle") {
        return (
            <div className="flex items-center gap-2" role="radiogroup" aria-label={t("language.switch")}>
                <Globe className="size-4 text-muted-foreground shrink-0" />
                {LOCALES.map((locale) => (
                    <button
                        key={locale}
                        onClick={() => switchLocale(locale)}
                        role="radio"
                        aria-checked={currentLocale === locale}
                        className={cn(
                            "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                            currentLocale === locale
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:text-foreground",
                        )}
                    >
                        {t(`language.${locale}`)}
                    </button>
                ))}
            </div>
        );
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                    aria-label={t("language.switch")}
                >
                    <Globe className="size-4" />
                    <span className="text-xs font-medium uppercase">{currentLocale}</span>
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                {LOCALES.map((locale) => (
                    <DropdownMenuItem
                        key={locale}
                        onClick={() => switchLocale(locale)}
                        className={currentLocale === locale ? "font-semibold" : ""}
                    >
                        {t(`language.${locale}`)}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
